import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MuiThemeProvider,
  MenuItem,
  Select,
  TextField,
  createMuiTheme,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  copyGameFrom,
  deleteGame,
  getGameById,
  getGames,
  postGame,
  updateGame,
} from "../../../../api/game";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useSkeleton } from "../../../hooks/useSkeleton";

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyGame() {
  return {
    fullName: "",
    durationMinutes: 45,
    hasVideo: true,
  };
}

export default function EditGamesPage() {
  const [game, setGame] = useState(getEmptyGame());
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [games, setGames] = useState([]);
  const [copyFromGameId, setCopyFromGameId] = useState("");
  const gameId = useParams().id;
  const history = useHistory();

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    if (!gameId) {
      getGames()
        .then((res) => {
          if (res.status === 200) setGames(res.data || []);
        })
        .catch(() => {});
      disableLoadingData();
      return;
    }
    getGameById(gameId)
      .then((res) => {
        if (res.status === 200) {
          setGame(res.data);
          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "Could not get game.",
        });
        history.push("/games");
      });
  }, [gameId, disableLoadingData, history]);

  function saveGame() {
    if (!gameId) {
      if (copyFromGameId) {
        copyGameFrom(copyFromGameId, { fullName: game.fullName })
          .then((res) => {
            if (res.status === 201) {
              alertSuccess({
                title: "Saved!",
                customMessage: "Game created with content copied from the selected game.",
              });
              history.push("/games");
            }
          })
          .catch((error) => {
            alertError({
              error: error,
              customMessage: "Could not create game from copy.",
            });
          });
      } else {
        postGame(game)
          .then((res) => {
            if (res.status === 201) {
              alertSuccess({
                title: "Saved!",
                customMessage: "Game successfully created.",
              });
              history.push("/games");
            }
          })
          .catch((error) => {
            alertError({
              error: error,
              customMessage: "Could not create game.",
            });
          });
      }
    } else {
      updateGame(gameId, game)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Changes successfully saved.",
            });
            history.push("/games");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  const handleChange = (element) => (event) => {
    setGame({ ...game, [element]: event.target.value });
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          <CardHeader title={gameId ? "Edit game" : "Create game"}></CardHeader>
          <CardBody>
            <TextField
              id="fullName"
              label="Full name"
              value={game.fullName}
              onChange={handleChange("fullName")}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              required
              fullWidth
            />
            <TextField
              id="durationMinutes"
              label="Duración total (minutos)"
              type="number"
              inputProps={{ min: 1, max: 999 }}
              value={game.durationMinutes ?? 45}
              onChange={(e) => setGame({ ...game, durationMinutes: Math.max(1, parseInt(e.target.value, 10) || 45) })}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
              helperText="Tiempo máximo del juego para el temporizador (ej. 45)"
            />
            {gameId && (
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={game.hasVideo !== false}
                    onChange={(e) =>
                      setGame({ ...game, hasVideo: e.target.checked })
                    }
                  />
                }
                label="Incluir pantalla de vídeo intro (/video) tras el registro"
              />
            )}
            {!gameId && games.length > 0 && (
              <FormControl margin="normal" fullWidth variant="outlined">
                <InputLabel id="copy-from-label">Crear a partir de (textos, imágenes, video, quizzes)</InputLabel>
                <Select
                  labelId="copy-from-label"
                  label="Crear a partir de (textos, imágenes, video, quizzes)"
                  value={copyFromGameId}
                  onChange={(e) => setCopyFromGameId(e.target.value)}
                >
                  <MenuItem value="">Nuevo juego vacío</MenuItem>
                  {games.map((g) => (
                    <MenuItem key={g._id} value={g._id}>{g.fullName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </CardBody>
        </Card>
        <Button
          onClick={() => history.push("/games")}
          variant="outlined"
          style={{ marginRight: "20px" }}
        >
          Back
        </Button>
        <Button
          onClick={() => saveGame()}
          variant="outlined"
          color="primary"
          style={{ marginRight: "20px" }}
        >
          Save game
        </Button>
        {gameId && (
          <>
            <MuiThemeProvider theme={theme}>
              <Button
                onClick={() => setOpenConfirmDialog(true)}
                variant="outlined"
                color="secondary"
              >
                Delete game
              </Button>
            </MuiThemeProvider>

            <ConfirmDialog
              title={"Are you sure you want to delete this game?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteGame(gameId)
                  .then((res) => {
                    if (res.status === 204) {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: "Game deleted successfully",
                      });
                      history.push("/games");
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not delete economic sector.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
